import { Loader2, RefreshCw, Database, Cloud, Server } from 'lucide-react';

interface SwitchingOverlayProps {
  cloudType?: string;
  cloudId?: string;
}

export function SwitchingOverlay({ cloudType, cloudId }: SwitchingOverlayProps) {
  const getCloudIcon = () => {
    switch (cloudType?.toUpperCase()) {
      case 'AWS':
        return <Database className="w-6 h-6 text-orange-500" />;
      case 'GCP':
        return <Cloud className="w-6 h-6 text-blue-500" />;
      case 'AZURE':
        return <Cloud className="w-6 h-6 text-cyan-500" />;
      default:
        return <Server className="w-6 h-6 text-primary" />;
    }
  };

  const getCloudName = () => {
    switch (cloudType?.toUpperCase()) {
      case 'AWS':
        return 'Amazon Web Services';
      case 'GCP':
        return 'Google Cloud Platform';
      case 'AZURE':
        return 'Microsoft Azure';
      default:
        return 'Cloud Provider';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 right-1/3 w-64 h-64 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-6 p-8">
        {/* Animated icons */}
        <div className="relative">
          {/* Outer spinning ring */}
          <div className="absolute inset-0 w-24 h-24 border-2 border-primary/20 rounded-full animate-spin" style={{ animationDuration: '3s' }} />
          <div className="absolute inset-1 w-22 h-22 border-2 border-dashed border-primary/30 rounded-full animate-spin" style={{ animationDuration: '4s', animationDirection: 'reverse' }} />
          
          {/* Center icon container */}
          <div className="w-24 h-24 rounded-full bg-card border border-border flex items-center justify-center shadow-lg">
            <div className="relative">
              {getCloudIcon()}
              <RefreshCw className="absolute -bottom-1 -right-1 w-4 h-4 text-primary animate-spin" />
            </div>
          </div>
        </div>

        {/* Text content */}
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            Switching Cloud Account
          </h3>
          <p className="text-sm text-muted-foreground max-w-xs">
            Loading {getCloudName()} resources
          </p>
          {cloudId && (
            <p className="text-xs font-mono text-muted-foreground/70 bg-muted/50 px-3 py-1 rounded-full">
              {cloudId}
            </p>
          )}
        </div>

        {/* Loading dots */}
        <div className="flex gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-primary/60 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
